/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.browser.composable.component

import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.forId
import org.jetbrains.compose.web.attributes.maxLength
import org.jetbrains.compose.web.attributes.name
import org.jetbrains.compose.web.attributes.readOnly
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.KeyboardEvent
import systems.frictionless.impulse.browser.id.HtmlId

private const val KEY_ENTER = 13

/**
 * Simplified version of [androidx.compose.material.TextField].
 */
@Composable
@Suppress("LongParameterList", "LongMethod", "ComplexMethod")
fun TextField(
    value: String,
    onValueChange: ((String) -> Unit)? = null,
    onEnterKey: (() -> Unit)? = null,
    type: Type? = null,
    @Suppress("UNUSED_PARAMETER") autoFocus: Boolean = false,
    maxLength: Int? = null,
    id: HtmlId? = null,
    label: @Composable (() -> Unit)? = null
) {
    label?.let {
        Label(attrs = {
            id?.let {
                forId(it.idString)
            }
        }) {
            label.invoke()
        }
    }
    val cursorPosition = remember { mutableStateOf<Int?>(null) }
    Div {
        val reference = remember { ReferenceHolder<HTMLInputElement>() }
        Input(
            type = type?.inputType ?: InputType.Text,
            attrs = {
                value(value)
                id?.let {
                    id(it.idString)
                }
                type?.fieldName?.let {
                    name(it)
                }
                ref {
                    reference.ref = it
                    onDispose {
                        reference.ref = null
                    }
                }
                type?.let { type ->
                    spellCheck(type.spellCheck)
                    attr("autocomplete", type.autoCompleteValue)
                    if (onValueChange == null) {
                        readOnly()
                    }
                    maxLength?.let {
                        maxLength(it)
                    }
                }
                onValueChange?.let {
                    onInput { event ->
                        val target = event.target
                        val selectionStart = target.asDynamic()?.selectionStart as? Int?
                        cursorPosition.value = selectionStart
                        val newText = target.value
                        onValueChange(newText)
                    }
                }
                // onKeyUp crashes with ClassCastException. This is workaround
                onEnterKey?.let {
                    addEventListener("keyup") { event ->
                        val nativeEvent = event.nativeEvent
                        if (nativeEvent is KeyboardEvent && nativeEvent.keyCode == KEY_ENTER) {
                            onEnterKey()
                        }
                    }
                }

                // TODO [#214] Implement autofocus properly.
                //  Implementation commented below is not reliable at the moment
                //  See https://stackoverflow.com/a/27314017

                // if (autoFocus) {
                // autoFocus()
                // }
            }
        )
        SideEffect {
            val targetInput = reference.ref
            targetInput?.let {
                val position = cursorPosition.value
                position?.let {
                    // hack. See https://stackoverflow.com/a/55803519 for more info
                    val oldType = targetInput.type
                    targetInput.type = "text"
                    targetInput.setSelectionRange(position, position)
                    targetInput.type = oldType
                }
            }
        }
    }
}

/* Currently, contains only subset of possible types.
   Exhaustive info at https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types

   Also see https://developers.google.com/web/updates/2015/06/checkout-faster-with-autofill for info related to fieldName
*/
enum class Type(
    val inputType: InputType<*>,
    val autoCompleteValue: String,
    val spellCheck: Boolean,
    val fieldName: String? = null
) {
    Text(inputType = InputType.Text, autoCompleteValue = "on", spellCheck = true),
    FirstName(inputType = InputType.Text, autoCompleteValue = "given-name", spellCheck = false, fieldName = "fname"),
    LastName(inputType = InputType.Text, autoCompleteValue = "family-name", spellCheck = false, fieldName = "lname"),
    Password(inputType = InputType.Password, autoCompleteValue = "current-password", spellCheck = false),
    NewPassword(inputType = InputType.Password, autoCompleteValue = "new-password", spellCheck = false),

    // Note: using Text because Email type prevents text selection
    Email(inputType = InputType.Text, autoCompleteValue = "email", spellCheck = false)
}

// More info at https://kotlinlang.slack.com/archives/C01F2HV7868/p1623946082296400?thread_ts=1623922411.290200&cid=C01F2HV7868
class ReferenceHolder<T>(var ref: T? = null)
